import type { DashboardAlertSound } from "./dashboardAlertSounds";

/**
 * The alerts the dashboard can raise in the corner while it is open, one entry per thing that can happen.
 * Every one starts ON; the person turns off the ones they do not want from Settings → Avisos en el dashboard.
 * A new entry here is a new switch there, on by default, with no migration: preferences store what was turned
 * OFF.
 */
export const DASHBOARD_ALERT_TYPES = [
  "payins_breb_key",
  "payins_collections",
  "payouts_paid",
  "payouts_failed",
  "payout_batches_done",
  "cards_purchase_approved",
  "cards_purchase_declined",
  "cards_refund",
] as const;

export type DashboardAlertType = (typeof DASHBOARD_ALERT_TYPES)[number];

/** The module an alert belongs to: what Settings groups them by, and what decides whether a person sees it. */
export type DashboardAlertGroup = "payins" | "payouts" | "payout_batches" | "cards";

/** Whether an alert is good news or a problem: what colors its icon and picks its section's falling sound. */
export type DashboardAlertOutcome = "good" | "problem";

export interface DashboardAlertDefinition {
  group: DashboardAlertGroup;
  outcome: DashboardAlertOutcome;
  /** The section's sound for this alert. */
  sound: DashboardAlertSound;
  /** For an alert whose outcome varies (a batch can finish with errors), the sound when it went wrong. */
  problemSound?: DashboardAlertSound;
}

export const DASHBOARD_ALERT_CATALOG: Record<DashboardAlertType, DashboardAlertDefinition> = {
  payins_breb_key: { group: "payins", outcome: "good", sound: "payin" },
  payins_collections: { group: "payins", outcome: "good", sound: "payin" },
  payouts_paid: { group: "payouts", outcome: "good", sound: "payout" },
  payouts_failed: { group: "payouts", outcome: "problem", sound: "payout_failed" },
  payout_batches_done: {
    group: "payout_batches",
    outcome: "good",
    sound: "batch",
    problemSound: "batch_failed",
  },
  cards_purchase_approved: { group: "cards", outcome: "good", sound: "card" },
  cards_purchase_declined: { group: "cards", outcome: "problem", sound: "card_failed" },
  cards_refund: { group: "cards", outcome: "good", sound: "card" },
};

/** The sound one alert plays: its section's, falling when this one went wrong. */
export function soundForAlert(type: DashboardAlertType, wentWrong: boolean): DashboardAlertSound {
  const definition = DASHBOARD_ALERT_CATALOG[type];
  return wentWrong && definition.problemSound ? definition.problemSound : definition.sound;
}

/** What Settings and the first-time banner let the person hear per section: how it sounds, and how it fails. */
export const DASHBOARD_ALERT_GROUP_SOUNDS: Record<DashboardAlertGroup, readonly DashboardAlertSound[]> = {
  payins: ["payin"],
  payouts: ["payout", "payout_failed"],
  payout_batches: ["batch", "batch_failed"],
  cards: ["card", "card_failed"],
};

export const DASHBOARD_ALERT_GROUPS: readonly {
  id: DashboardAlertGroup;
  types: readonly DashboardAlertType[];
}[] = (["payins", "payouts", "payout_batches", "cards"] as const).map((id) => ({
  id,
  types: DASHBOARD_ALERT_TYPES.filter((type) => DASHBOARD_ALERT_CATALOG[type].group === id),
}));
